module Meevo.Plugins.Register {

    export interface VisitNoteServiceModel {
        clientId: string;
        client: Model.SaleClient;
        employee: Model.EmployeeListItem;
        visitNoteTypeId: string;
        visitNoteType?: Model.VisitNoteType;
        serviceDisplayName: string;
        noteText?: string;
        productUsage?: Meevo.Model.ISaleLineServiceProductUsage;
    }

    export class VisitNoteServiceController {
        static $inject = [
            "meevo.components.common.DataService",
            "meevo.components.common.SecurityService",
            "$mdDialog",
            "$translate",
            "$q",
            "$sce"
        ];

        public visitNoteModels: VisitNoteServiceModel[];
        public incomingModels: VisitNoteServiceModel[];
        public selectedVisitNoteModel: VisitNoteServiceModel;
        public currentVisitNoteModel: VisitNoteServiceModel;
        public currentModelExistingNotes: any;
        public showExistingNotes = false;
        private isVishEnabled = false;
        private htmlEditorConfig: Components.Common.IWysiwygConfig;

        constructor(
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected securityService: Meevo.Components.Common.ISecurityService,
            protected $mdDialog: angular.material.IDialogService,
            protected $translate: angular.translate.ITranslateService,
            protected $q: angular.IQService,
            protected $sce: angular.ISCEService
        ) {
            this.securityService.checkModuleAccess(Meevo.Components.Common.SecurityKeys.INTEGRATION_VISH_INTERNAL).then((response) => {
                this.isVishEnabled = response === Model.SecurityAccessEnum.Grant;

                this.setupClientVisitNoteItems(this.incomingModels).then((items: VisitNoteServiceModel[]) => {
                    this.visitNoteModels = _(items).sortBy(item => item.serviceDisplayName);
                    this.currentVisitNoteModel = this.selectedVisitNoteModel = this.visitNoteModels[0];
                });
            });

            this.htmlEditorConfig = {
                toolbar: [
                    { name: 'basicStyling', items: ['bold', 'italic', 'underline'] },
                    { name: "paragraph", items: ["orderedList", "unorderedList", "-"] }
                ],
            };
        }

        private getProductUsageText(visitNoteModel: VisitNoteServiceModel) {
            // if VISH enabled, then exit
            if (this.isVishEnabled) {
                return '';
            }

            const productUsage = visitNoteModel.productUsage;

            if (!productUsage ||
                !productUsage.variants ||
                productUsage.variants.length === 0) {
                return '';
            }

            let productUsageLines = [];

            for (const variant of productUsage.variants) {
                const variantDisplayName = variant.variantDisplayName ? ` - ${variant.variantDisplayName}` : '';
                let unitOfMeasureText;
                switch (variant.productUsageAmountType) {
                    case Meevo.Model.ProductUsageAmountTypeEnum.WholeProduct:
                        unitOfMeasureText = this.$translate.instant("LABEL_EACH");
                        break;
                    case Meevo.Model.ProductUsageAmountTypeEnum.PartialAmount:
                        unitOfMeasureText = this.$translate.instant(`ENUM_LABEL_${variant.unitOfMeasure}`);
                        break;
                }
                const productUsageLine = `${variant.productSku}: ${variant.productDisplayName}${variantDisplayName} - ${variant.usageAmount} ${unitOfMeasureText}`;
                productUsageLines.push(productUsageLine);
            }

            return productUsageLines.join('<br>');
        }

        private getDefaultNoteText(visitNoteModel: VisitNoteServiceModel, templateText: string): string {
            let noteText = templateText || '';
            let productUsageText = this.getProductUsageText(visitNoteModel);
            if (templateText && productUsageText) {
                noteText += '<br><br>';
            }
            noteText += productUsageText;
            return noteText;
        }

        setupClientVisitNoteItems(visitNoteModels: VisitNoteServiceModel[]) {
            const promises = _(visitNoteModels).chain().map(model => model.visitNoteTypeId).unique()
                .map(visitNoteTypeId => this.dataService.getItem("visitnotetype/" + visitNoteTypeId)).value();

            return this.$q.all(promises).then(visitNoteTypeResponse => {
                const visitNoteTypes = _(visitNoteTypeResponse).flatten();

                visitNoteModels.forEach(visitNoteModel => {
                    visitNoteModel.visitNoteType = visitNoteTypes.find(visitNoteType => visitNoteType.id === visitNoteModel.visitNoteTypeId);
                    visitNoteModel.noteText = this.getDefaultNoteText(visitNoteModel, visitNoteModel.visitNoteType.templateText);
                });

                return visitNoteModels;
            });
        }

        changeSelection() {
            this.save().then(() => {
                this.showExistingNotes = false;
                this.currentVisitNoteModel = this.selectedVisitNoteModel;
                this.currentVisitNoteModel.noteText = this.getDefaultNoteText(this.currentVisitNoteModel, this.selectedVisitNoteModel.visitNoteType.templateText);
            });
        }

        useExistingNotes(showExistingNotes: boolean): void {
            if (showExistingNotes && !this.currentModelExistingNotes) {
                this.getExistingNotesForCurrentModel();
            }
            this.showExistingNotes = showExistingNotes;
            if (!showExistingNotes) {
                let defaultNoteText = this.getDefaultNoteText(this.currentVisitNoteModel, this.selectedVisitNoteModel.visitNoteType.templateText);

                if (this.currentVisitNoteModel.noteText.length > 0 && this.currentVisitNoteModel.noteText == defaultNoteText) {
                    this.currentVisitNoteModel.noteText = '';
                }
                this.currentVisitNoteModel.noteText += (this.currentVisitNoteModel.noteText.length > 0 ? '<br>' : '') + defaultNoteText;
            }
        }

        getExistingNotesForCurrentModel() {
            const url = `api/client/${this.currentVisitNoteModel.clientId}/visitnotes`;
            const request = {
                criteria: {
                    visitNoteType: this.currentVisitNoteModel.visitNoteType
                },
                itemsPerPage: 25,
                pageNumber: 0,
                sortBy: 'DateCreated',
                sortDirection: Model.SortDirection.Descending
            }
            this.currentModelExistingNotes = new Components.BaseClasses.PagedResults(this.dataService, url);
            this.currentModelExistingNotes.initSearch(request).then(() => {});
        }

        replaceNoteText(note: any) {
            if (!note.noteText || note.noteText.length < 1) {
                return;
            }

            this.currentVisitNoteModel.noteText = note.noteText;
        }

        expandNote(note) {
            note.isExpanded = !note.isExpanded;
        }

        close() {
            this.$mdDialog.hide();
        }

        save(): ng.IPromise<void> {
            const item = this.currentVisitNoteModel;
            if (!item.noteText || item.noteText.trim().length < 1) {
                this.currentModelExistingNotes = null;
                return this.$q.resolve();
            }

            const url = `client/${item.clientId}/visitnotes`;

            const request = {
                visitNoteType: item.visitNoteType,
                employee: item.employee,
                isExtendedNote: false,
                noteText: item.noteText,
                isFormatted: 1,
                dateVisited: moment().format()
            };

            return this.dataService.add(url, request)
                .then(response => {
                    this.currentModelExistingNotes = null;
                    this.currentVisitNoteModel.noteText = '';
                });
        }

        saveAndContinue(): void {
            this.save().then(() => {
                this.showExistingNotes = false;
                const currentGroupIndex = this.visitNoteModels.indexOf(this.currentVisitNoteModel);
                this.currentVisitNoteModel = this.selectedVisitNoteModel = this.visitNoteModels[currentGroupIndex+1];
            });
        }

        saveAndClose(): void {
            this.save().then(() => this.close());
        }

        trustHtml(noteText) {
            return this.$sce.trustAsHtml(noteText);
        }
    }

    angular.module("meevo.plugins.register")
        .controller("meevo.plugins.register.VisitNoteServiceController", VisitNoteServiceController);
}